import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CredenzaBody, CredenzaFooter } from "@/components/ui/credenza";
import { Label } from "@/components/ui/label";
import { Link } from "@/components/ui/link";
import { LoadingButton } from "@/components/ui/loading-button";
import { Separator } from "@/components/ui/separator";
import { useGetLoggedInUser } from "@/hooks/use-get-logged-in-user";
import { useScopedI18n } from "@/locales/client";
import { isEuCountry, isTinRequired } from "@talentir/shared/countries";
import { addressFieldsToArray } from "@talentir/shared/helpers";
import { Pencil } from "lucide-react";
import { useState } from "react";

export function Confirm({
    method,
    currency,
    amount,
    grossAmount,
    isLoading,
    onClick,
    goBack,
    onAddressEdit,
    onBankDetailsEdit,
    onVatEdit,
}: {
    method: "bank" | "crypto";
    currency: "USD" | "EUR";
    amount: string;
    grossAmount?: string;
    isLoading: boolean;
    onClick: () => void;
    goBack: () => void;
    onAddressEdit: () => void;
    onBankDetailsEdit: () => void;
    onVatEdit: () => void;
}) {
    const t = useScopedI18n("paymentDialogs.common.confirm");
    const { user } = useGetLoggedInUser();

    if (user == null) return null;

    const tinMissing =
        user.countryCode != null
            ? isTinRequired(user.countryCode) &&
              user.taxpayerIdentificationNumber == null &&
              user.europeanVatNumber == null
            : false;

    return (
        <>
            <CredenzaBody className="space-y-2 md:space-y-3">
                <div className="text-sm">
                    <div className="font-semibold">{t("details.payoutMethod.title")}</div>
                    <div className="text-muted-foreground">
                        {method === "crypto"
                            ? t("details.payoutMethod.wallet", { currency: currency === "EUR" ? "EURC" : "USDC" })
                            : t("details.payoutMethod.bank")}
                    </div>
                </div>
                <Separator />
                {method === "crypto" && (
                    <div className="text-sm">
                        <div className="font-semibold">{t("details.wallet.title")}</div>
                        <div className="text-muted-foreground">{user.id}</div>
                    </div>
                )}
                {method === "bank" && (
                    <div className="flex items-center justify-between text-sm">
                        <div className="text-sm">
                            <div className="font-semibold">{t("details.bankAccount.title")}</div>
                            {user.bankAccountIban == null && user.revolutAccountId == null && (
                                <div className="font-semibold text-error">{t("details.bankAccount.error")}</div>
                            )}
                            {user.revolutAccountId === "NOT YET SET" && (
                                <div className="font-semibold text-error">
                                    Please contact support to set your bank details
                                </div>
                            )}
                            {user.bankAccountIban != null && (
                                <div className="text-muted-foreground">{user.bankAccountIban}</div>
                            )}
                            {user.revolutAccountId != null && (
                                <div className="text-muted-foreground">Manual Account</div>
                            )}
                        </div>
                        <Button variant="outline" size="sm" onClick={onBankDetailsEdit}>
                            {t("buttons.edit")} <Pencil className="ml-2 size-3" />
                        </Button>
                    </div>
                )}
                <Separator />
                <div className="flex items-center justify-between text-sm">
                    <div>
                        <div className="font-semibold">{t("details.address.title")}</div>
                        <div className="text-muted-foreground">
                            {addressFieldsToArray(user) == null ? (
                                <div className="font-semibold text-error">{t("details.address.error")}</div>
                            ) : (
                                <>
                                    {addressFieldsToArray(user)?.map((e, i) => (
                                        <div key={i}>{e}</div>
                                    ))}
                                </>
                            )}
                        </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={onAddressEdit}>
                        {t("buttons.edit")} <Pencil className="ml-2 size-3" />
                    </Button>
                </div>
                {isEuCountry(user.countryCode ?? "") && (
                    <>
                        <Separator />
                        <div className="flex items-center justify-between text-sm">
                            <div>
                                <div className="font-semibold">{t("details.vat.title")}</div>
                                <div className="text-muted-foreground">
                                    {tinMissing ? (
                                        <div className="font-semibold text-error">{t("details.vat.tinMissing")}</div>
                                    ) : user.europeanVatNumber == null ? (
                                        <>
                                            <div>{t("details.vat.taxExempt")}</div>
                                            {user.taxpayerIdentificationNumber != null && (
                                                <div>Steuernummer: {user.taxpayerIdentificationNumber}</div>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <div>{user.europeanVatNumber}</div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <Button variant="outline" size="sm" onClick={onVatEdit}>
                                {t("buttons.edit")} <Pencil className="ml-2 size-3" />
                            </Button>
                        </div>
                    </>
                )}
                <Separator />
                <div className="grid grid-cols-2">
                    <div className="text-sm">
                        <div className="font-semibold">{t("details.payoutAmount.title")}</div>
                        <div className="text-muted-foreground">{amount}</div>
                    </div>
                    {grossAmount != null && grossAmount !== amount && (
                        <div className="text-sm">
                            <div className="font-semibold">{t("details.payoutAmountGross.title")}</div>
                            <div className="text-muted-foreground">{grossAmount}</div>
                        </div>
                    )}
                </div>
            </CredenzaBody>
            <CredenzaFooter>
                <Button onClick={() => goBack()} variant="outline">
                    {t("buttons.goBack")}
                </Button>
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <LoadingButton
                            disabled={
                                user.countryCode == null ||
                                (method === "bank" &&
                                    user.bankAccountIban == null &&
                                    (user.revolutAccountId == null || user.revolutAccountId === "NOT YET SET")) ||
                                tinMissing
                            }
                            loading={isLoading}
                        >
                            {t("buttons.claim")}
                        </LoadingButton>
                    </AlertDialogTrigger>

                    <AlertDialogContent>
                        <ConfirmAlert
                            onClaimButtonClicked={() => {
                                onClick();
                            }}
                        />
                    </AlertDialogContent>
                </AlertDialog>
            </CredenzaFooter>
        </>
    );
}

function ConfirmAlert({ onClaimButtonClicked }: { onClaimButtonClicked: () => void }) {
    const t = useScopedI18n("paymentDialogs.common.confirm.alert");
    const [accepted, setAccepted] = useState(false);

    return (
        <>
            <AlertDialogHeader>
                <AlertDialogTitle>{t("title")}</AlertDialogTitle>
                <AlertDialogDescription>{t("description")}</AlertDialogDescription>
            </AlertDialogHeader>
            <div className="flex items-center gap-2 leading-none">
                <Checkbox
                    id="terms-and-conditions"
                    checked={accepted}
                    onCheckedChange={(v) => {
                        setAccepted(v === true);
                    }}
                />
                <Label htmlFor="terms-and-conditions" className="inline leading-snug">
                    {t("terms", {
                        link: (
                            <Link className="underline" href="/legal/terms-and-conditions">
                                {t("link")}
                            </Link>
                        ),
                    })}
                </Label>
            </div>
            <AlertDialogFooter>
                <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                <AlertDialogAction asChild>
                    <Button disabled={!accepted} onClick={onClaimButtonClicked}>
                        {t("confirm")}
                    </Button>
                </AlertDialogAction>
            </AlertDialogFooter>
        </>
    );
}
